import { Download, FileText } from 'lucide-react';
import type { Report } from '@/api/types';
import { DownloadButton } from '@/components/DownloadButton';
import { formatWhen } from '@/lib/format';

/** A published report as the client reads it; the studio opens the same view. */
export function ReportArticle({ report, download, downloadPdf }: { report: Report; download: (documentId: string) => Promise<Blob>; downloadPdf: () => Promise<Blob> }) {
  return (
    <article className="rounded-lg p-6 shadow-hair">
      <p className="text-xs text-muted">
        {report.period}, {report.author.name}, {report.publishedAt ? formatWhen(report.publishedAt) : 'черновик — клиент его не видит'}
      </p>
      <p className="mt-2 text-md font-semibold leading-snug">{report.summary}</p>
      {report.numbers.length > 0 && (
        <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded bg-line sm:grid-cols-4">
          {report.numbers.map((n) => (
            <div key={n.label} className="bg-canvas p-3">
              <dt className="text-xs text-muted">{n.label}</dt>
              <dd className="num mt-0.5 text-lg font-semibold">{n.value}</dd>
              {n.change && <dd className="text-xs text-muted">{n.change}</dd>}
            </div>
          ))}
        </dl>
      )}
      <div className="mt-5 grid gap-6 sm:grid-cols-2">
        <div>
          <h3 className="mb-1.5 text-sm font-semibold">Сделали</h3>
          <ul className="list-disc space-y-1 pl-4 text-sm">
            {report.done.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="mb-1.5 text-sm font-semibold">Дальше</h3>
          <ul className="list-disc space-y-1 pl-4 text-sm">
            {report.next.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <DownloadButton className="btn-secondary h-8 gap-2 px-3 text-sm" name={`Отчёт ${report.period}.pdf`} load={downloadPdf} label="Скачать PDF">
          <Download className="size-4" /> Скачать PDF
        </DownloadButton>
      </div>
      {report.documentId ? (
        <div className="mt-3">
          <DownloadButton className="btn-secondary h-8 gap-2 px-3 text-sm" name={report.fileName ?? `Отчёт ${report.period}.pptx`} load={() => download(report.documentId!)}>
            <FileText className="size-4" /> {report.fileName ?? 'Скачать отчёт'}
          </DownloadButton>
        </div>
      ) : (
        report.fileName && (
          <p className="mt-5 flex items-center gap-2 text-sm text-muted">
            <FileText className="size-4" /> {report.fileName}
          </p>
        )
      )}
    </article>
  );
}
